import {
  Button,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  Progress,
  Text,
  Title,
} from "@mantine/core";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { useGetPoll, useGetPollResults } from "../../api/polls";

const toReadableText = (value) => {
  const text = (value ?? "").toString().trim().replace(/\s+/gu, " ");

  return text.charAt(0).toUpperCase() + text.slice(1);
};

const PollResults = () => {
  const { id } = useParams();
  const pollId = id;

  const location = useLocation();
  const navigate = useNavigate();
  const isPresentationMode =
    location?.state?.isPresentationMode ||
    new RegExp(`^/app/polls/${pollId}/results/present$`).test(
      location.pathname
    );

  // In presentation mode the page renders outside the AppShell (no navbar), so
  // navigate to a deterministic destination instead of relying on history,
  // which breaks on refresh. Esc exits, mirroring the visible button below.
  useEffect(() => {
    if (!isPresentationMode) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        navigate(`/app/polls/${pollId}/results`);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isPresentationMode, navigate, pollId]);

  const { data: pollData, isLoading: isLoadingPoll } = useGetPoll(pollId);
  const { data: results, isLoading, error } = useGetPollResults(pollId);

  const voteCounts = useMemo(() => {
    const counts = Object.create(null);

    if (results?.length > 0) {
      for (const result of results) {
        for (const ans of result.answer) {
          if (!ans.text) {
            const key = ans.id.toString();
            counts[key] = counts[key] || { label: key, count: 0 };
            counts[key].count += 1;
            continue;
          }

          const text = ans.text.toString().trim().replace(/\s+/gu, " ");
          if (!text) {
            continue;
          }

          // Group answers case-, punctuation- and diacritic-insensitively
          // (šta ≡ sta). Serbian Cyrillic is unaffected, so it never merges
          // into its Latin transliteration.
          const normalized = text
            .toLowerCase()
            .normalize("NFKD")
            .replace(/[^\p{L}\p{N}]/gu, "");
          const key = normalized || text.toLowerCase();

          // The key is stripped of punctuation and diacritics, so it must never
          // be what the room sees projected.
          counts[key] = counts[key] || {
            label: toReadableText(text),
            count: 0,
          };
          counts[key].count += 1;
        }
      }
    }

    return counts;
  }, [results]);

  if (isLoading) {
    return <Text>Loading...</Text>;
  }

  if (error) {
    return <Text c="red">Error: {error.message}</Text>;
  }

  function renderResults(pollData) {
    if (pollData.type === "text") {
      return (
        <div
          style={{
            marginBottom: 20,
          }}
        >
          {Object.entries(voteCounts)
            .sort(([, a], [, b]) => b.count - a.count)
            .map(([key, { label, count }]) => (
              <div key={key}>
                <Text>
                  {label} - {count} {count === 1 ? "vote" : "votes"}
                </Text>
                <Text></Text>
              </div>
            ))}
        </div>
      );
    }
    return pollData?.options.map((option) => {
      const optionVotes = voteCounts[option.id]?.count || 0;
      const votesPercentage = ((optionVotes / results.length) * 100).toFixed(2);
      return (
        <div key={option.id}>
          <Text fw={500}>{option.title}</Text>
          <Group>
            <Text>
              <span>({optionVotes}</span> votes)
            </Text>
            <Text>{votesPercentage}%</Text>
          </Group>
          <Progress
            mt={5}
            mb={20}
            value={votesPercentage}
            max={100}
            color="blue"
            radius="xl"
          />
        </div>
      );
    });
  }

  if (isLoadingPoll || isLoading) {
    return (
      <Center>
        <Loader />
      </Center>
    );
  }

  return (
    <Container style={isPresentationMode && styles.container}>
      {isPresentationMode && (
        <Button
          variant="default"
          style={styles.exitButton}
          onClick={() => navigate(`/app/polls/${pollId}/results`)}
        >
          Exit presentation
        </Button>
      )}
      <Container
        style={{
          width: 500,
        }}
        my={20}
      >
        <Paper withBorder shadow="md" p="md">
          <Title size="lg" mb={20}>
            {pollData?.title}
          </Title>
          <Text>{pollData?.description}</Text>
          {Object.keys(voteCounts).length === 0 ? (
            <Text mt="md" mb={20}>
              No results available yet for this poll.
            </Text>
          ) : (
            renderResults(pollData)
          )}
          {!isPresentationMode && (
            <Link
              style={styles.link}
              state={{
                isPresentationMode: true,
              }}
              to={`/app/polls/${pollId}/results/present`}
            >
              Present results
            </Link>
          )}
        </Paper>
      </Container>
    </Container>
  );
};

export default PollResults;

const styles = {
  container: {
    display: "grid",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
  },
  link: {
    backgroundColor: "#1554F6",
    color: "white",
    textDecoration: "none",
    padding: "10px 20px",
    borderRadius: 5,
  },
  exitButton: {
    position: "fixed",
    top: 20,
    left: 20,
    zIndex: 10,
  },
};
